import React from 'react';
import styled from 'styled-components';
import Modal from '@digital-hig/mui/dist/Modal';
import PropTypes from 'prop-types';
import Typography from '@digital-hig/mui/dist/Typography';
import SvgIcon from '@mui/material/SvgIcon';
import Button from '@mui/material/Button';
import Benefits from '@digital-hig/icon/lib/esm/icons/benefits';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { LoginClickHandler } from '../common/login';
import { triggerManualLinkClick } from '../common/analyticsApi';
import { LEARNING_MODE_COURSEWARE, LEARNING_MODE_MICROLEARNING } from '../common/constants';
import { dismissLogin, getShowLoginPrompt } from '../store/userSlice';

// eslint-disable-next-line consistent-return
const LoginModal = ({ learningMode, overviewUri }) => {
  const dispatch = useDispatch();
  const showLoginPrompt = useSelector(getShowLoginPrompt);
  const handleCloseLoginPrompt = () => {
    dispatch(dismissLogin(true));
  };

  // Modal is being loaded by a tutorial page and user cannot preview tutorials

  if (learningMode === LEARNING_MODE_COURSEWARE) {
    const options = ['Save your progress', 'Take assessments', 'Receive personalized recommendations'];
    return (
      <Modal
        open
        closeButtonDisplay={false}
        content={(
          <InnerModel>
            <Typography variant="headline-small" className="title">Sign in to start learning</Typography>
            <Typography component="span" variant="body-copy-medium" className="subtitle">Sign in for unlimited free access to all learning content.</Typography>
            <div className="options">
              {options.map((item) => (
                <div className="single-option">
                  <SvgIcon fontSize="large" className="icon">
                    <Benefits />
                  </SvgIcon>
                  <Typography variant="body-copy-medium" className="option-title">{item}</Typography>
                </div>
              ))}
            </div>
            <div className="button-wrapper">
              <Button onClick={() => { triggerManualLinkClick({ linkTitle: 'Sign in or create an account' }); LoginClickHandler(); }}>Sign in or create an account</Button>
              <Button variant="text" component={Link} onClick={() => { triggerManualLinkClick({ linkTitle: 'Go to overview' }); }} to={overviewUri}>Go to overview </Button>
            </div>
          </InnerModel>
        )}
      />
    );
  } if (learningMode === LEARNING_MODE_MICROLEARNING && showLoginPrompt) {
    const options = ['Save your progress', 'Get access to courses', 'Receive personalized recommendations'];
    return (
      <Modal
        open
        closeButtonDisplay={false}
        content={(
          <InnerModel>
            <Typography variant="headline-small" className="title">Sign in for the best experience</Typography>
            <div className="options">
              {options.map((item) => (
                <div className="single-option">
                  <SvgIcon fontSize="large" className="icon">
                    <Benefits />
                  </SvgIcon>
                  <Typography variant="body-copy-medium" className="option-title">{item}</Typography>
                </div>
              ))}
            </div>
            <div className="button-wrapper">
              <Button onClick={() => { triggerManualLinkClick({ linkTitle: 'Sign in and continue learning' }); LoginClickHandler(); }}>Sign in and continue learning</Button>
              <Button variant="text" onClick={() => { triggerManualLinkClick({ linkTitle: 'No thanks' }); handleCloseLoginPrompt(); }}>No thanks</Button>
            </div>
          </InnerModel>
        )}
      />
    );
  }

  // User can still preview the current tutorial so do not display modal
  return <></>;
};

const InnerModel = styled.div`
  display: flex;
  flex-direction: column;

  .title {
    padding-bottom: 24px;
  }

  .subtitle {
    padding-top: 16px;
  }

  .options {
    padding-top: 20px;
    padding-bottom: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    .single-option {
      display: flex;
      flex-direction: row;
      gap: 12px;
    }
  }

  .button-wrapper {
    padding-top: 24px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 8px;
    
  }

  @media screen and (max-width: 560px) {

    width: 100%;
    
    .icon {
        width: 16px;
        height: 21px;
    }

    .options {
        padding-top: 8px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
    
    .single-option {
        display:flex;
        align-items: flex-start;
    }

    .button-wrapper {
      padding-top: 12px;
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
   }
`;

LoginModal.propTypes = {
  learningMode: PropTypes.string.isRequired,
  overviewUri: PropTypes.string,
};

LoginModal.defaultProps = {
  overviewUri: '',
};

export default LoginModal;
